import { FilesTableEnum } from '../../enums/FilesTableEnum';
import type File from '../../interfaces/File';
import { dateFromTicks, formatDate, formatFileSize, msToTicks } from '../utils';

/** Column keys selectable for folder file-list export. */
export type ExportFolderFileListColumn =
  | 'id'
  | 'name'
  | 'type'
  | 'size'
  | 'lastModified'
  | 'fileType'
  | 'isShared'
  | 'createdBy'
  | 'sharedBy'
  | 'tags';

/** Export download format (CSV / JSON / SpreadsheetML). */
export type ExportFolderFileListFormat = 'csv' | 'json' | 'excelXml';

/** CSV field delimiter (not used inside tag cells). */
export type ExportFolderFileListDelimiter = ',' | ';' | '\t' | '|';

/** Separator between tag titles inside a single tags cell. */
export type ExportFolderFileListTagJoin = ';' | '|' | ',';

/** Presets for formatting .NET-tick dates in export cells. */
export type ExportFolderFileListDateFormat =
  'localeShort' | 'iso' | 'localeDateTime' | 'dmy' | 'mdy' | 'isoDateTime' | 'europeanDateTime';

/** Fixed sample ticks for date-format option labels (2026-09-09 14:30 UTC). */
export const EXPORT_FOLDER_FILE_LIST_SAMPLE_DATE_TICKS = msToTicks(Date.UTC(2026, 8, 9, 14, 30, 0));

/** UTF-8 BOM for Excel-friendly CSV downloads. */
export const CSV_UTF8_BOM = '\uFEFF';

/**
 * Default column order for export UI + builders.
 * `createdAt` omitted: `/myfiles/v2` list rows do not populate it (detail UI loads via GET `/myfiles/:id`).
 * Folder `fileSize` left blank: only per-folder `getFolderContentStats` exists (N recursive calls — skip).
 * `createdBy` / `sharedBy` available everywhere; default-checked only on Received.
 */
export const EXPORT_FOLDER_FILE_LIST_COLUMNS: ExportFolderFileListColumn[] = [
  'id',
  'name',
  'type',
  'size',
  'lastModified',
  'fileType',
  'isShared',
  'createdBy',
  'sharedBy',
  'tags',
];

/** Columns default-checked only when exporting from Received. */
export const EXPORT_FOLDER_FILE_LIST_RECEIVED_ONLY_DEFAULT_COLUMNS: ExportFolderFileListColumn[] = [
  'createdBy',
  'sharedBy',
];

/** Default checked columns for the export form (Received includes createdBy/sharedBy). */
export function getDefaultSelectedExportFolderFileListColumns(
  section?: FilesTableEnum | null,
): ExportFolderFileListColumn[] {
  if (section === FilesTableEnum.Received) {
    return [...EXPORT_FOLDER_FILE_LIST_COLUMNS];
  }
  return EXPORT_FOLDER_FILE_LIST_COLUMNS.filter(
    (column) => !EXPORT_FOLDER_FILE_LIST_RECEIVED_ONLY_DEFAULT_COLUMNS.includes(column),
  );
}

/** Default tag-cell join separator (independent of CSV delimiter unless user picks same char). */
export const EXPORT_FOLDER_FILE_LIST_TAGS_JOIN: ExportFolderFileListTagJoin = ';';

/** Max display length for folder name in export modal title. */
export const EXPORT_FOLDER_FILE_LIST_TITLE_FOLDER_MAX = 40;

/** Shared cell / label options for all export builders. */
export type ExportFolderFileListBuildOptions = {
  columns: ExportFolderFileListColumn[];
  /** Localized header labels keyed by column. */
  headers: Partial<Record<ExportFolderFileListColumn, string>>;
  /** Localized label for folders in the type column. */
  folderLabel: string;
  /** Localized yes/no for boolean columns (isShared). */
  yesLabel: string;
  noLabel: string;
  /** Join separator for multiple tags in one cell. Defaults to semicolon. */
  tagsJoin?: ExportFolderFileListTagJoin;
  /** Format tick timestamps; defaults to locale short date. */
  dateFormatter?: (ticks: number) => string;
  /** Resolve tag id → display title; unknown ids fall back to String(id). */
  tagTitleById?: (tagId: number) => string | undefined;
};

export type ExportFolderFileListCsvOptions = ExportFolderFileListBuildOptions & {
  /** CSV field delimiter. Defaults to comma. */
  delimiter?: ExportFolderFileListDelimiter;
};

/** Excel-safe CSV field escape (quote + double embedded quotes). Always applied. */
export function escapeCsvField(value: string | number): string {
  const text = String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/** Escape text for SpreadsheetML / XML attribute and element content. */
export function escapeXmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function padTwoDigits(value: number): string {
  return String(value).padStart(2, '0');
}

/** UTC fixed layouts — stable across locales; matches ISO date base. */
function formatUtcFixed(
  date: Date,
  layout: 'dmy' | 'mdy' | 'isoDateTime' | 'europeanDateTime',
): string {
  const year = date.getUTCFullYear();
  const month = padTwoDigits(date.getUTCMonth() + 1);
  const day = padTwoDigits(date.getUTCDate());
  const hour = padTwoDigits(date.getUTCHours());
  const minute = padTwoDigits(date.getUTCMinutes());
  switch (layout) {
    case 'dmy':
      return `${day}/${month}/${year}`;
    case 'mdy':
      return `${month}/${day}/${year}`;
    case 'isoDateTime':
      return `${year}-${month}-${day} ${hour}:${minute}`;
    case 'europeanDateTime':
      return `${day}/${month}/${year} ${hour}:${minute}`;
  }
}

/** Build a date formatter for the selected export date preset. */
export function createExportFolderFileListDateFormatter(
  format: ExportFolderFileListDateFormat,
  locale?: string | null,
): (ticks: number) => string {
  return (ticks: number) => {
    const date = dateFromTicks(ticks);
    switch (format) {
      case 'iso':
        return date.toISOString().slice(0, 10);
      case 'localeDateTime':
        return date.toLocaleString(locale ?? undefined);
      case 'dmy':
      case 'mdy':
      case 'isoDateTime':
      case 'europeanDateTime':
        return formatUtcFixed(date, format);
      case 'localeShort':
      default:
        return formatDate(ticks, locale);
    }
  };
}

function typeCell(file: File, folderLabel: string): string {
  if (file.isFolder) return folderLabel;
  if (file.fileExtension) return file.fileExtension;
  return '';
}

function tagsCell(
  file: File,
  tagTitleById?: (tagId: number) => string | undefined,
  tagsJoin: ExportFolderFileListTagJoin = EXPORT_FOLDER_FILE_LIST_TAGS_JOIN,
): string {
  if (!file.tags?.length) return '';
  return file.tags.map((tag) => tagTitleById?.(tag.id) ?? String(tag.id)).join(tagsJoin);
}

function cellValue(
  file: File,
  column: ExportFolderFileListColumn,
  options: Pick<
    ExportFolderFileListBuildOptions,
    'folderLabel' | 'tagTitleById' | 'yesLabel' | 'noLabel' | 'dateFormatter' | 'tagsJoin'
  >,
): string {
  const formatTicks = options.dateFormatter ?? ((ticks: number) => formatDate(ticks));

  switch (column) {
    case 'id':
      return file.id != null ? String(file.id) : '';
    case 'name':
      return file.fileName ?? '';
    case 'type':
      return typeCell(file, options.folderLabel);
    case 'size':
      // Folders: list API has no size; getFolderContentStats is 1 heavy call per folder — leave blank.
      return file.isFolder ? '' : formatFileSize(file.fileSize ?? 0);
    case 'lastModified':
      return file.lastModifiedAt ? formatTicks(file.lastModifiedAt) : '';
    case 'fileType':
      if (file.isFolder) return options.folderLabel;
      return file.fileType ? String(file.fileType) : '';
    case 'isShared':
      return file.isShared ? options.yesLabel : options.noLabel;
    case 'createdBy':
      return file.createdBy ?? '';
    case 'sharedBy':
      return file.sharedBy ?? '';
    case 'tags':
      return tagsCell(file, options.tagTitleById, options.tagsJoin);
    default:
      return '';
  }
}

function resolveColumns(options: ExportFolderFileListBuildOptions): ExportFolderFileListColumn[] {
  return options.columns.filter((column) => options.headers[column] != null);
}

/**
 * Build CSV string for a folder file list (headers + rows).
 * Empty `files` still emits a header row when columns are selected.
 * Every cell is quote-escaped so delimiters/commas inside tags are safe.
 * Caller should prepend {@link CSV_UTF8_BOM} on the download blob for Excel.
 */
export function buildExportFolderFileListCsv(
  files: File[],
  options: ExportFolderFileListCsvOptions,
): string {
  const columns = resolveColumns(options);
  if (columns.length === 0) {
    return '';
  }

  const delimiter = options.delimiter ?? ',';
  const headerLine = columns
    .map((column) => escapeCsvField(options.headers[column]!))
    .join(delimiter);
  const rowLines = files.map((file) =>
    columns.map((column) => escapeCsvField(cellValue(file, column, options))).join(delimiter),
  );

  return [headerLine, ...rowLines].join('\n');
}

/**
 * Build JSON array of objects keyed by stable camelCase column ids
 * (`id`, `name`, `lastModified`, …) — not localized headers (those stay for CSV/Excel).
 * Empty `files` still emits `[]` when columns are selected (no header-only object).
 */
export function buildExportFolderFileListJson(
  files: File[],
  options: ExportFolderFileListBuildOptions,
): string {
  const columns = resolveColumns(options);
  if (columns.length === 0) {
    return '[]';
  }

  const rows = files.map((file) => {
    const row: Record<string, string> = {};
    for (const column of columns) {
      row[column] = cellValue(file, column, options);
    }
    return row;
  });

  return JSON.stringify(rows, null, 2);
}

/**
 * Build SpreadsheetML 2003 XML Excel opens without SheetJS.
 * Prefer `.xls` + `application/vnd.ms-excel` for association.
 */
export function buildExportFolderFileListSpreadsheetMl(
  files: File[],
  options: ExportFolderFileListBuildOptions,
): string {
  const columns = resolveColumns(options);
  if (columns.length === 0) {
    return '';
  }

  const cellXml = (text: string) =>
    `<Cell><Data ss:Type="String">${escapeXmlText(text)}</Data></Cell>`;

  const headerRow = `<Row>${columns.map((column) => cellXml(options.headers[column]!)).join('')}</Row>`;
  const dataRows = files
    .map(
      (file) =>
        `<Row>${columns.map((column) => cellXml(cellValue(file, column, options))).join('')}</Row>`,
    )
    .join('');

  return (
    `<?xml version="1.0"?>\n` +
    `<?mso-application progid="Excel.Sheet"?>\n` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"` +
    ` xmlns:o="urn:schemas-microsoft-com:office:office"` +
    ` xmlns:x="urn:schemas-microsoft-com:office:excel"` +
    ` xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"` +
    ` xmlns:html="http://www.w3.org/TR/REC-html40">` +
    `<Worksheet ss:Name="FileList"><Table>${headerRow}${dataRows}</Table></Worksheet>` +
    `</Workbook>`
  );
}

const FILENAME_EXTENSION: Record<ExportFolderFileListFormat, string> = {
  csv: 'csv',
  json: 'json',
  excelXml: 'xls',
};

/** Sanitize folder display name for use in a download filename. */
export function buildExportFolderFileListFilename(
  folderName?: string | null,
  format: ExportFolderFileListFormat = 'csv',
): string {
  const base = (folderName?.trim() || 'root')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .replace(/\s+/g, ' ')
    .slice(0, 80)
    .trim();
  const extension = FILENAME_EXTENSION[format];
  return `${base || 'root'}-file-list.${extension}`;
}

/** Truncate folder name for modal title display (ellipsis when over max). */
export function truncateExportFolderFileListTitleFolder(
  folderName: string,
  max = EXPORT_FOLDER_FILE_LIST_TITLE_FOLDER_MAX,
): string {
  const trimmed = folderName.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1))}…`;
}
